'use client';

import { useEffect, useRef } from 'react';
import { useMap } from '@vis.gl/react-google-maps';

interface StrategyProfileCircleContentProps {
  center: { lat: number; lng: number } | null;
  radius: number;
  isVisible: boolean;
}

/**
 * StrategyProfileCircleContent Component
 *
 * Renders a radius circle on the map for NOT_REFERRING strategies.
 * This component follows the same pattern as Discovery's CircleContent component
 * but is read-only (non-editable, non-draggable).
 *
 * @param center - The center point of the circle (source provider location)
 * @param radius - The radius in meters
 * @param isVisible - Whether the circle should be visible
 */
const StrategyProfileCircleContent = ({
  center,
  radius,
  isVisible,
}: StrategyProfileCircleContentProps) => {
  const map = useMap();
  const currentCircleRef = useRef<google.maps.Circle | null>(null);

  useEffect(() => {
    if (!map) {
      return;
    }

    if (typeof google === 'undefined' || !google?.maps) {
      return;
    }

    // Clean up existing circle if conditions not met
    if (!isVisible || !center || !radius) {
      if (currentCircleRef.current) {
        currentCircleRef.current.setMap(null);
        currentCircleRef.current = null;
      }
      return;
    }

    try {
      // Create or update circle
      if (!currentCircleRef.current) {
        // Create new circle
        const circle = new google.maps.Circle({
          strokeColor: '#000000',
          strokeOpacity: 0, // No border
          strokeWeight: 0, // No border
          fillColor: '#000000',
          fillOpacity: 0.16, // 16% opacity (#00000029)
          editable: false, // Read-only for viewing
          draggable: false, // Read-only for viewing
          radius: radius,
          center: center,
          map: map, // Set map directly in constructor
        });

        currentCircleRef.current = circle;
      } else {
        // Update existing circle
        currentCircleRef.current.setRadius(radius);
        currentCircleRef.current.setCenter(center);
      }
    } catch (error) {
      console.error('[StrategyProfileCircleContent] Error creating circle:', error);
    }

    // Cleanup on unmount or when dependencies change
    return () => {
      if (currentCircleRef.current) {
        currentCircleRef.current.setMap(null);
        currentCircleRef.current = null;
      }
    };
  }, [map, center, radius, isVisible]);

  return null;
};

export default StrategyProfileCircleContent;
